import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getMyTeam } from "../api/agent";

/**
 * The signed-in user's team: the agents and assistants on their desk.
 *
 * Christian Gravias, Monday 13225574145, 7 Oct 2026: Spiros has George and
 * Paul in his team and should get a My Listings switcher ("All agents,
 * Spiros, Paul & George") and Agent Performance for the three of them.
 *
 * Fetched once per signed-in user and shared, because the sidebar, the
 * Agent Performance route and My Listings all ask the same question.
 * Returns { members, hasTeam, loaded }; members is [] for anyone with no team.
 */
let cache = { userId: null, promise: null, members: null };

const load = (userId) => {
  if (cache.userId !== userId || !cache.promise) {
    cache = { userId, promise: null, members: null };
    cache.promise = getMyTeam()
      .then((res) => {
        const members = res.data?.members || [];
        if (cache.userId === userId) cache.members = members;
        return members;
      })
      .catch(() => {
        // Let the next mount try again rather than caching a failure.
        if (cache.userId === userId) cache.promise = null;
        return [];
      });
  }
  return cache.promise;
};

export default function useMyTeam() {
  const userId = useSelector((state) => state.auth?.user?.id) || null;
  const cached = userId && cache.userId === userId ? cache.members : null;
  const [members, setMembers] = useState(cached || []);
  const [loaded, setLoaded] = useState(Boolean(cached));

  useEffect(() => {
    if (!userId) {
      setMembers([]);
      setLoaded(true);
      return undefined;
    }
    let alive = true;
    load(userId).then((list) => {
      if (!alive) return;
      setMembers(list);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [userId]);

  return { members, hasTeam: members.length > 1, loaded };
}
